import { ChatDetailsResponse } from '@choco-admin/types';
import { Button, Chip } from '@heroui/react';
import React from 'react';
import { FiCopy } from 'react-icons/fi';

import { useNotification } from '../../../../../contexts/NotificationContext';

interface EditChatHeaderProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const EditChatHeader: React.FC<EditChatHeaderProps> = ({
  chatData,
  chatId,
}) => {
  const { addNotification } = useNotification();
  const handleCopyChatId = async () => {
    try {
      await navigator.clipboard.writeText(chatId);
      addNotification({
        title: 'Chat ID Copied',
        message: 'Chat ID has been copied to clipboard.',
        type: 'success',
      });
    } catch (error) {
      addNotification({
        title: 'Copy Failed',
        message: 'Failed to copy chat ID to clipboard.',
        type: 'error',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'primary';
    }
  };

  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-foreground">Edit Chat</h1>
          <Chip
            color={getStatusColor(chatData.status)}
            variant="flat"
            size="sm"
          >
            {chatData.status.charAt(0).toUpperCase() + chatData.status.slice(1)}
          </Chip>
        </div>

        <div className="space-y-1 text-foreground-600">
          <p className="text-lg font-medium">{chatData.restaurantName}</p>
          <p className="text-sm">
            {chatData.supplierEntity?.name || 'Unknown Supplier'}
          </p>
          {chatData.address && <p className="text-sm">{chatData.address}</p>}
        </div>

        <div className="flex items-center gap-2 mt-3">
          <span className="text-sm">
            Chat ID:{' '}
            <span
              className="text-primary hover:text-primary-600 cursor-pointer"
              onClick={handleCopyChatId}
            >
              {chatId}
            </span>
          </span>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            onPress={handleCopyChatId}
            aria-label="Copy Chat ID"
          >
            <FiCopy className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
